import React, { useCallback, useEffect, useState } from "react";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import { renderHourTick } from "../../../components/recharts/components";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import { DynamicDatePicker } from "../../../components/DatePicker";
import { FilterTab } from "../../../components/Filters";
import Loading from "../../../components/Loading";
import { DataTabs } from "../../Hppc/Demand/Data";
import { getLastWeekDateRange } from "./PriceForcastingRTM";
import { BrushStart } from "../../../models/chart_model";
import { getPriceForecastIEXDaily } from "../../../Rest_api/RestApi";
import { formatDateToLocal } from "./PriceForcastingIEXDAM";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import { IntervalFilter } from "./PriceForecastIEXWrapper";

const chartSuffix = [
  {
    label: "Forecast",
    value: "forecasted_mcp",
  },
  {
    label: "Actual (Avg)",
    value: "actual_mcp",
  },
  {
    label: "Actual (Wtg. Avg)",
    value: "actual_wt_avg_mcp",
  },
];

const IexDaily = ({
  selectedTab,
  setSelectedTab,
  dataTab,
  setDataTab,
}: {
  selectedTab: any;
  setSelectedTab: any;
  dataTab: any;
  setDataTab: any;
}) => {
  const [loading, setLoading] = useState(false);
  const today = new Date();
  const startOfCurrentMonth = new Date(
    today.getFullYear(),
    today.getMonth(),
    1,
    0,
    0,
    0,
    0
  );
  const endOfCurrentMonth = new Date(
    today.getFullYear(),
    today.getMonth() + 1,
    0,
    23,
    59,
    59,
    999
  );

  const [dates, setDate] = useState({
    startDate: startOfCurrentMonth,
    endDate: endOfCurrentMonth,
    key: "selection",
  });

  const [data, setData] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [tableData, setTableData] = useState<any[]>([]);

  const handleFetch = useCallback(async () => {
    setLoading(true);
    try {
      const startDateStr = formatDateToLocal(dates.startDate);
      const endDateStr = formatDateToLocal(dates.endDate);
      const newData = await getPriceForecastIEXDaily(startDateStr, endDateStr);

      const transformedData = newData.map((item: any) => ({
        ...item,
        actual_mcp: item.actual_mcp,
        forecasted_mcp: item.forecasted_mcp,
      }));
      setData(transformedData);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, [dates.endDate, dates.startDate]);

  useEffect(() => {
    handleFetch();
  }, [handleFetch]);

  useEffect(() => {
    let filteredData = data;
    const par: ChartDataParams = {
      keys: ["date"],
      tableKeys: ["date"],
      groupBy: ["product__name"],
      volumeKey: ``,
      aggregationKeys: ["forecasted_mcp", "actual_mcp", "actual_wt_avg_mcp"],
      priceKey: "",
      data: filteredData,
      reverseSort: ["date"],
    };
    const cData = FormatChartData(par);
    setChartData(cData.data);
    setChartKeys(cData.keys);
    setTableData(cData.tableData || []);
  }, [data]);

  const createChartObject = (
    key: string,
    index: number,
    value: string,
    label: string
  ) => {
    return {
      dataKey: `${key}_${value}`,
      name: `${key
        .replace("DAM", " ")
        .replace("_", " ")
        .replace("undefined", "")} ${label}`,
      legendColor: undefined,
    };
  };

  function getLegends() {
    return chartSuffix.flatMap(({ label, value }) =>
      chartKeys.map(
        (key, index) => createChartObject(key, index, value, label) as any
      )
    );
  }

  const tablePar = {
    legends: getLegends(),
    data: chartData,
    tableData: tableData,
    itemsPerPage: 96,
    unit: "Rs/KWh",
    extraKeys: ["date"],
    extraLabels: ["Date"],
    filename: "IEX_Daily_Price_Forecast",
    showTotal: false,
  };

  return (
    <>
      <div className="gas-page-container">
        {loading && <Loading />}

        <div className="gas-chart-container">
          <div className="gas-heading gap-x-10">
            {/* <h2 className="chartHeading w-60">Price Forecast (Rs/KWh)</h2> */}
            <h2 className="chartHeading w-60">DAM Price Forecast (IEX)</h2>
            <div className="flex gap-x-6  justify-end">
              <FilterTab
                options={IntervalFilter}
                selected={[selectedTab]}
                onClick={(option, index) => {
                  setSelectedTab(option);
                }}
              />
              <FilterTab
                options={DataTabs.map((item) => item.label)}
                selected={[dataTab.label]}
                onClick={(option, index) => {
                  setDataTab(DataTabs[index]);
                }}
              />

              <DynamicDatePicker
                setDate={(date) => setDate(date[0]!)!}
                defaultDates={dates}
                minDate="2025-03-13"
              />

              {/* <button
                onClick={() => {
                  dataTab.label === "Chart" &&
                    downloadChartTableCSV(tablePar as any);

                  dataTab.label === "Summary" &&
                    downloadSummaryCSV(tablePar as any);

                  dataTab.label === "Table" &&
                    downloadChartTableCSV(tablePar as any);
                }}
                className="downloadButton"
              >
                Download Data
              </button> */}
            </div>
          </div>
          <div className="h-full w-full relative">
            {dataTab.label === "Chart" && (
              <>
                <ReMixChart
                  chartContainerHeight="calc(100vh - 250px)"
                  data={chartData}
                  showBrush={chartData.length > 96 ? true : undefined}
                  brushStart={BrushStart.Start}
                  isTimeSlot={false}
                  onlyTitle={true}
                  unit={"Rs/KWh"}
                  yAxisLabel={"Rs/KWh"}
                  nameGap={40}
                  yMaxValue={10}
                  legends={getLegends() as any}
                  xDataKey={"date"}
                  xTick={(value: any, index: any) => {
                    return renderHourTick(value, 10);
                  }}
                  secondXDataKey={undefined}
                />
              </>
            )}
            {dataTab.label === "Summary" && (
              <LegendSummaryTable {...(tablePar as any)} />
            )}
            {dataTab.label === "Table" && <ChartTable {...(tablePar as any)} />}
          </div>
        </div>
      </div>
    </>
  );
};

export default IexDaily;
